import {
  TableContainer,
  CardBody,
  Card,
  Button,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalHeader,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
} from '@chakra-ui/react'
import {DataTable} from "../components/dataTable";
import {createColumnHelper} from "@tanstack/react-table";
import {useAppDataStore} from "../mobx/appDataStore";
import {useEffect, useMemo, useState} from "react";
import {useFormik} from "formik";
import {Navigate} from "react-router-dom";
import {EditIcon} from "@chakra-ui/icons";

const columnHelper = createColumnHelper();

const CreateUser = ({onCreated}) => {
  const {isOpen, onOpen, onClose} = useDisclosure()
  const appDataStore = useAppDataStore()
  const {socket} = appDataStore
  const toast = useToast()
  const [error, setError] = useState(null)
  const [roles, setRoles] = useState([])

  useEffect(() => {
    socket.emit('datasource', {model: 'Rol', records: 100, currentPage: 1, searchAttributes: []}, (res) => setRoles(res.data || []))
  }, [])

  const formik = useFormik({
    initialValues: {email: '', name: '', password: '', rolId: ''},
    onSubmit: async (values) => {
      try {
        setError(null)
        await appDataStore.post('/auth/signup', values)
        formik.resetForm()
        onClose()
        onCreated()
        toast({title: 'Usuario creado', status: 'success', duration: 5000, isClosable: true})
      } catch (err) {
        setError(err.message)
      }
    }
  })

  return (
    <>
      <Button colorScheme='blue' onClick={onOpen} mb={5}>
        Crear usuario
      </Button>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay/>
        <ModalContent>
          <ModalHeader>Crear usuario</ModalHeader>
          <ModalCloseButton/>
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel>Nombre</FormLabel>
              <Input name={'name'} onChange={formik.handleChange} value={formik.values.name}/>
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Email</FormLabel>
              <Input name={'email'} onChange={formik.handleChange} value={formik.values.email}/>
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Contraseña</FormLabel>
              <Input type={'password'} name={'password'} onChange={formik.handleChange}
                     value={formik.values.password}/>
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Rol</FormLabel>
              <Select name={'rolId'} placeholder={'Seleccione un rol'} onChange={formik.handleChange}
                      value={formik.values.rolId}>
                {roles.map(rol => <option key={rol.id} value={rol.id}>{rol.name}</option>)}
              </Select>
            </FormControl>
            {error && <p style={{color: 'red'}}>{error}</p>}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme='blue' mr={3} onClick={formik.submitForm}>Crear</Button>
            <Button onClick={onClose}>Cancelar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

const EditUser = ({user, onClose, onEdited}) => {
  const appDataStore = useAppDataStore()
  const {socket} = appDataStore
  const toast = useToast()
  const [error, setError] = useState(null)
  const [roles, setRoles] = useState([])

  useEffect(() => {
    socket.emit('datasource', {model: 'Rol', records: 100, currentPage: 1, searchAttributes: []}, (res) => setRoles(res.data || []))
  }, [])

  const formik = useFormik({
    initialValues: {rolId: user.Rol?.id || '', blocked: user.blocked, mailModuleEnabled: !!user.mailModuleEnabled},
    onSubmit: async (values) => {
      try {
        setError(null)
        await appDataStore.post('/auth/editUser', {id: user.id, ...values})
        onEdited()
        onClose()
        toast({title: 'Usuario editado', status: 'success', duration: 5000, isClosable: true})
      } catch (err) {
        setError(err.message)
      }
    }
  })

  return (
    <Modal isOpen={true} onClose={onClose}>
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>Editar usuario: {user.name}</ModalHeader>
        <ModalCloseButton/>
        <ModalBody pb={6}>
          <FormControl>
            <FormLabel>Rol</FormLabel>
            <Select name={'rolId'} onChange={formik.handleChange} value={formik.values.rolId}>
              {roles.map(rol => <option key={rol.id} value={rol.id}>{rol.name}</option>)}
            </Select>
          </FormControl>
          <FormControl mt={4}>
            <Checkbox name={'blocked'} isChecked={formik.values.blocked} onChange={formik.handleChange}>Bloqueado</Checkbox>
          </FormControl>
          <FormControl mt={4}>
            <Checkbox name={'mailModuleEnabled'} isChecked={formik.values.mailModuleEnabled} onChange={formik.handleChange}>
              Acceso al módulo de correos
            </Checkbox>
            {!['distribuidor', 'subadmin'].includes((roles.find(r => r.id === Number(formik.values.rolId))?.name || '').toLowerCase()) && (
              <p style={{color: 'gray', fontSize: '0.85em'}}>Solo tiene efecto en usuarios con rol distribuidor o subadmin.</p>
            )}
          </FormControl>
          {error && <p style={{color: 'red'}}>{error}</p>}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme='blue' mr={3} onClick={formik.submitForm}>Guardar</Button>
          <Button onClick={onClose}>Cancelar</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const Users = () => {
  const appDataStore = useAppDataStore()
  const {socket} = appDataStore
  const [data, setData] = useState([])
  const [refreshDependencies, setRefreshDependencies] = useState(0)
  const [editingUser, setEditingUser] = useState(null)

  const dataFetch = async (records, currentPage, order, search) => {
    return new Promise(resolve => {
      socket.emit('datasource', {
        model: 'User',
        records,
        currentPage,
        order,
        search,
        searchAttributes: ['name', 'email'],
        include: ['Rol']
      }, resolve)
    })
  }

  const columns = useMemo(() => ([
    columnHelper.accessor('id', {cell: (info) => info.getValue(), header: "ID"}),
    columnHelper.accessor('name', {cell: (info) => info.getValue(), header: "Nombre"}),
    columnHelper.accessor('email', {cell: (info) => info.getValue(), header: "Email"}),
    columnHelper.accessor('Rol.name', {cell: (info) => info.getValue(), header: "Rol"}),
    columnHelper.accessor('blocked', {cell: (info) => info.getValue() ? 'Bloqueado' : 'Activo', header: "Estado"}),
    columnHelper.accessor('mailModuleEnabled', {cell: (info) => info.getValue() ? 'Sí' : 'No', header: "Módulo correos"}),
    columnHelper.accessor('', {
      cell: (info) => <Button size={'sm'} colorScheme='teal' variant={'outline'} onClick={() => setEditingUser(info.row.original)}><EditIcon/></Button>,
      header: "Editar"
    }),
  ]), [])

  if (appDataStore.user?.rol?.name?.toLowerCase() !== 'admin') return <Navigate to="/admin" replace/>

  return (
    <Card m={5}>
      <CardBody>
        <CreateUser onCreated={() => setRefreshDependencies(Math.random())}/>
        <TableContainer>
          <DataTable size={'sm'} columns={columns} data={data} dataFetch={dataFetch}
                     refreshDependencies={refreshDependencies}/>
        </TableContainer>
        {editingUser && (
          <EditUser
            user={editingUser}
            onClose={() => setEditingUser(null)}
            onEdited={() => setRefreshDependencies(Math.random())}
          />
        )}
      </CardBody>
    </Card>
  )
}

export default Users
